import React, { useState, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './DataTable.css';

const STATUS_COLORS = {
  success: 'success', pending: 'warning', failed: 'danger', timeout: 'gray',
};

export default function Transactions() {
  const { showToast } = useToast();
  const [rows,       setRows]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [events,     setEvents]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const limit = 20;

  useEffect(() => {
    api.get('/admin/events', { params: { limit: 100 } })
      .then(r => setEvents(r.data.events || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/transactions', { params: { page, limit, search, status, event_id: eventFilter } })
      .then(r => {
        setRows(r.data.transactions);
        setTotal(r.data.pagination?.total || 0);
        setSummary(r.data.summary || {});
      })
      .catch(() => showToast('Failed to load transactions', 'error'))
      .finally(() => setLoading(false));
  }, [page, search, status, eventFilter, showToast]);

  const totalPages = Math.ceil(total / limit);

  const fmt = (n, cur) => `${cur || ''} ${parseFloat(n || 0).toFixed(2)}`.trim();

  return (
    <Layout>
      <div className="data-table-page">
        <div className="page-header">
          <div>
            <h1>Transactions</h1>
            <p>Payment history for all paid events</p>
          </div>
        </div>

        {/* Summary */}
        <div className="stats-bar">
          {[
            { lbl:'Total Revenue',    val: fmt(summary.total_revenue, 'GHS'),  cls:'accent' },
            { lbl:'Successful',       val: summary.successful  || 0 },
            { lbl:'Pending',          val: summary.pending  || 0 },
            { lbl:'Failed / Timeout', val: (summary.failed || 0) + (summary.total_timeout || 0), cls:'warm' },
          ].map(({ lbl, val, cls }) => (
            <div className={`stat-card-sm ${cls||''}`} key={lbl}>
              <span className="val">{val}</span>
              <span className="lbl">{lbl}</span>
            </div>
          ))}
        </div>

        <div className="toolbar">
          <div className="search-box">
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              placeholder="Search by reference, email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className="filter-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="timeout">Timeout</option>
          </select>
          <select className="filter-select" value={eventFilter} onChange={e => { setEventFilter(e.target.value); setPage(1); }}>
            <option value="">All Events</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
          </select>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="table-loading"><div className="spinner" /></div>
          ) : rows.length === 0 ? (
            <div className="empty-state"><p>No transactions found.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Name</th>
                  <th>Event</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id}>
                    <td>
                      <code style={{fontSize:'0.78rem', color:'var(--text-muted)'}}>{t.transaction_ref}</code>
                    </td>
                    <td>
                      <div className="name-cell">
                        <div className="avatar-sm">{t.firstname?.[0]}{t.lastname?.[0]}</div>
                        <div>
                          <div>{t.firstname} {t.lastname}</div>
                          <div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{t.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{maxWidth:'180px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                      {t.event_title || '—'}
                    </td>
                    <td style={{fontWeight:600}}>{t.currency} {parseFloat(t.amount || 0).toFixed(2)}</td>
                    <td>
                      <span className="badge badge-gray" style={{textTransform:'none'}}>
                        {t.mode_of_payment === 'momo_pay' ? '📱 MoMo' :
                         t.mode_of_payment === 'bank_pay' ? '🏦 Bank' :
                         t.mode_of_payment === 'card_pay' ? '💳 Card' : t.mode_of_payment || '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${STATUS_COLORS[t.status] || 'gray'}`}>{t.status}</span>
                    </td>
                    <td className="text-muted">
                      {new Date(t.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                    </td>
                    <td>
                      <button className="btn-xs btn-xs-outline" onClick={() => setSelected(t)}>Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const p = page <= 4 ? i + 1 : page + i - 3;
              return p > 0 && p <= totalPages ? p : null;
            }).filter(Boolean).map(p => (
              <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        )}

        {/* Transaction Detail Modal */}
        {selected && (
          <div className="modal-overlay" onClick={() => setSelected(null)}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
              <h3>Transaction Details</h3>
              <div className={`txn-status-banner ${STATUS_COLORS[selected.status] || 'gray'}`}>
                <span className={`badge badge-${STATUS_COLORS[selected.status] || 'gray'}`} style={{fontSize:'0.9rem', padding:'0.4rem 1rem'}}>
                  {selected.status?.toUpperCase()}
                </span>
                <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1.4rem', marginTop:'0.5rem'}}>
                  {selected.currency} {parseFloat(selected.amount || 0).toFixed(2)}
                </div>
              </div>
              <div className="detail-grid" style={{marginTop:'1rem'}}>
                {[
                  ['Transaction Ref',    selected.transaction_ref],
                  ['Tuameka Ref',        selected.tuameka_transaction_id],
                  ['Network Ref',        selected.network_transaction_id],
                  ['Customer Name',      `${selected.firstname} ${selected.lastname}`],
                  ['Email',             selected.email],
                  ['Phone',             `${selected.country_code || ''} ${selected.phone || ''}`],
                  ['Event',             selected.event_title],
                  ['Session',           selected.session_label || selected.session_date],
                  ['Payment Method',    selected.mode_of_payment],
                  ['Bank Account',      selected.bank_account_number],
                  ['Bank Name',         selected.bank_name],
                  ['Paid At',           selected.paid_at ? new Date(selected.paid_at).toLocaleString() : '—'],
                  ['Created At',        new Date(selected.created_at).toLocaleString()],
                ].filter(([, v]) => v).map(([lbl, val]) => (
                  <div className="detail-row" key={lbl}>
                    <span className="detail-lbl">{lbl}</span>
                    <span className="detail-val" style={{wordBreak:'break-all'}}>{val}</span>
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .txn-status-banner { text-align:center; padding:1.25rem; background:var(--gray-50); border-radius:var(--radius); margin-bottom:0.5rem; }
        .detail-grid { display:flex; flex-direction:column; gap:0; }
        .detail-row { display:flex; justify-content:space-between; padding:0.5rem 0; border-bottom:1px solid var(--border); gap:1rem; }
        .detail-lbl { font-size:0.78rem; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em; flex-shrink:0; }
        .detail-val { font-size:0.875rem; color:var(--text-primary); text-align:right; }
      `}</style>
    </Layout>
  );
}
